const ACCESS_TOKEN_PARAM = 'motito_access_token';
const REFRESH_TOKEN_PARAM = 'motito_refresh_token';
const PARTNER_CODE_PARAM = 'motito_partner_code';
const EXPIRES_IN_PARAM = 'motito_expires_in';
const SHOP_AUTH_HANDOFF_STORAGE_KEY = 'motito_shop_auth_handoff_v1';
const SHOP_AUTH_HANDOFF_TTL_MS = 15 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function normalizeString(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export interface ShopAuthHandoffPayload {
  accessToken: string;
  refreshToken?: string;
  partnerCode?: string;
  expiresIn?: number;
  cleanUrl: string;
}

interface StoredShopAuthHandoffPayload extends ShopAuthHandoffPayload {
  createdAt: number;
}

function clearStoredShopAuthHandoffPayload(storage: StorageLike | null): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(SHOP_AUTH_HANDOFF_STORAGE_KEY);
  } catch {
    // Storage persistence is best-effort.
  }
}

function getStorage(storage?: StorageLike): StorageLike | null {
  if (storage) {
    return storage;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function scrubShopAuthHandoffUrl(url: URL): string {
  const scrubbed = new URL(url.toString());
  scrubbed.hash = '';
  return `${scrubbed.pathname}${scrubbed.search}${scrubbed.hash}`;
}

export function extractShopAuthHandoffPayload(
  url: URL
): ShopAuthHandoffPayload | null {
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (!hash) {
    return null;
  }

  const hashParams = new URLSearchParams(hash);
  const accessToken = normalizeString(hashParams.get(ACCESS_TOKEN_PARAM));
  if (!accessToken) {
    return null;
  }

  const expiresInRaw = normalizeString(hashParams.get(EXPIRES_IN_PARAM));
  const parsedExpiresIn = expiresInRaw ? Number(expiresInRaw) : NaN;
  const expiresIn =
    Number.isFinite(parsedExpiresIn) && parsedExpiresIn > 0
      ? Math.floor(parsedExpiresIn)
      : undefined;

  return {
    accessToken,
    refreshToken: normalizeString(hashParams.get(REFRESH_TOKEN_PARAM)),
    partnerCode: normalizeString(hashParams.get(PARTNER_CODE_PARAM))?.toUpperCase(),
    expiresIn,
    cleanUrl: scrubShopAuthHandoffUrl(url),
  };
}

export function persistShopAuthHandoffPayload(
  payload: ShopAuthHandoffPayload,
  storage?: StorageLike
): void {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return;
  }

  try {
    const record: StoredShopAuthHandoffPayload = {
      ...payload,
      createdAt: Date.now(),
    };
    resolvedStorage.setItem(
      SHOP_AUTH_HANDOFF_STORAGE_KEY,
      JSON.stringify(record)
    );
  } catch {
    // Storage persistence is best-effort.
  }
}

export function readShopAuthHandoffPayload(
  storage?: StorageLike
): ShopAuthHandoffPayload | null {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return null;
  }

  try {
    const raw = resolvedStorage.getItem(SHOP_AUTH_HANDOFF_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredShopAuthHandoffPayload>;
    const accessToken = normalizeString(parsed.accessToken);
    const refreshToken = normalizeString(parsed.refreshToken);
    const partnerCode = normalizeString(parsed.partnerCode)?.toUpperCase();
    const cleanUrl = normalizeString(parsed.cleanUrl);
    const createdAt = Number(parsed.createdAt ?? 0);
    const expiresIn =
      typeof parsed.expiresIn === 'number' &&
      Number.isFinite(parsed.expiresIn) &&
      parsed.expiresIn > 0
        ? Math.floor(parsed.expiresIn)
        : undefined;

    if (!accessToken || !cleanUrl || createdAt <= 0) {
      clearStoredShopAuthHandoffPayload(resolvedStorage);
      return null;
    }

    if (Date.now() - createdAt > SHOP_AUTH_HANDOFF_TTL_MS) {
      clearStoredShopAuthHandoffPayload(resolvedStorage);
      return null;
    }

    return {
      accessToken,
      refreshToken: refreshToken ?? undefined,
      partnerCode: partnerCode ?? undefined,
      expiresIn,
      cleanUrl,
    };
  } catch {
    clearStoredShopAuthHandoffPayload(resolvedStorage);
    return null;
  }
}

export function clearShopAuthHandoffPayload(storage?: StorageLike): void {
  clearStoredShopAuthHandoffPayload(getStorage(storage));
}
